import "../../components/EventUserDashboard.css";

import React, { useEffect, useMemo, useState } from "react";
import { Select } from "@mantine/core";

interface EventDto {
    eventID: number;
    eventName?: string;
}

interface DrinkTicketCollectionDto {
    drinkTicketCollectionId: number;
    collectionName: string;
    bar: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    createdAt?: string;
    validDate?: string | null;
}

interface DrinkTicketDto {
    drinkTicketId: number;
    ticketCode: string;
    collectionName: string;
    ticketNumber: number;
    bar: number;
    maxDrinks: number;
    isActive: boolean;
}

enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return `Bar ${bar}`;
    }
};

const toArray = <T,>(result: any): T[] => {
    if (Array.isArray(result)) return result;
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.items)) return result.items;
    if (Array.isArray(result?.results)) return result.results;
    return [];
};

const toNonNegativeNumber = (value: string | number): number => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return 0;
    return Math.floor(num);
};

const readErrorMessage = async (res: Response, fallback: string) => {
    try {
        const contentType = res.headers.get("content-type") || "";

        if (contentType.includes("application/json")) {
            const json = await res.json();

            return (
                json?.message ||
                json?.error ||
                json?.title ||
                json?.detail ||
                JSON.stringify(json)
            );
        }

        const text = await res.text();
        return text || fallback;
    } catch {
        return fallback;
    }
};

const formatDate = (value?: string | null) => {
    if (!value) return "Alle dage";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("da-DK");
};

const getTicketStatusLabel = (isActive: boolean) => {
    return isActive ? "Aktiv" : "Brugt op";
};

const getTicketStatusStyle = (isActive: boolean) => {
    return {
        fontWeight: 700,
        color: isActive ? "#007a41" : "#b0000d"
    };
};

const AdminDrinkTicketCollections: React.FC = () => {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [collectionName, setCollectionName] = useState("");
    const [ticketCount, setTicketCount] = useState<number>(20);
    const [maxDrinksPerTicket, setMaxDrinksPerTicket] = useState<number>(1);
    const [bar, setBar] = useState<string | null>(String(Bar.BAR1));
    const [validDate, setValidDate] = useState<string>("");

    const [collections, setCollections] = useState<DrinkTicketCollectionDto[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number>(0);
    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingCollections, setIsLoadingCollections] = useState(false);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);

    useEffect(() => {
        const loadEvents = async () => {
            try {
                const res = await fetch("/api/events");
                if (!res.ok) return;

                const result = await res.json();
                setEvents(toArray<EventDto>(result));
            } catch (err) {
                console.error("Fejl ved hentning af events:", err);
            }
        };

        loadEvents();
    }, []);

    const loadCollections = async (currentEventId: number) => {
        if (!currentEventId) {
            setCollections([]);
            return;
        }

        try {
            setIsLoadingCollections(true);
            const res = await fetch(`/api/admin/drink-ticket-collections?eventId=${currentEventId}`);

            if (!res.ok) {
                const msg = await readErrorMessage(res, "Kunne ikke hente collections");
                throw new Error(msg);
            }

            const result = await res.json();
            setCollections(toArray<DrinkTicketCollectionDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af collections:", err);
            setError(err?.message || "Kunne ikke hente collections");
            setCollections([]);
        } finally {
            setIsLoadingCollections(false);
        }
    };

    const loadTickets = async (collectionId: number) => {
        if (!collectionId) {
            setTickets([]);
            return;
        }

        try {
            setIsLoadingTickets(true);
            const res = await fetch(`/api/admin/drink-ticket-collections/${collectionId}/tickets`);

            if (!res.ok) {
                const msg = await readErrorMessage(res, "Kunne ikke hente billetter");
                throw new Error(msg);
            }

            const result = await res.json();
            setTickets(toArray<DrinkTicketDto>(result));
        } catch (err: any) {
            console.error("Fejl ved hentning af billetter:", err);
            setError(err?.message || "Kunne ikke hente billetter");
            setTickets([]);
        } finally {
            setIsLoadingTickets(false);
        }
    };

    useEffect(() => {
        setSelectedCollectionId(0);
        setTickets([]);
        setMessage(null);
        setError(null);

        if (eventId > 0) {
            loadCollections(eventId);
        } else {
            setCollections([]);
        }
    }, [eventId]);

    useEffect(() => {
        if (selectedCollectionId > 0) {
            loadTickets(selectedCollectionId);
        } else {
            setTickets([]);
        }
    }, [selectedCollectionId]);

    const selectedCollection = useMemo(() => {
        return collections.find((x) => x.drinkTicketCollectionId === selectedCollectionId) ?? null;
    }, [collections, selectedCollectionId]);

    const submit = async () => {
        if (!eventId) {
            setError("Vælg event");
            return;
        }

        if (!collectionName.trim()) {
            setError("Skriv et navn til collection");
            return;
        }

        if (!bar) {
            setError("Vælg bar");
            return;
        }

        if (ticketCount <= 0) {
            setError("Antal billetter skal være større end 0");
            return;
        }

        if (maxDrinksPerTicket < 0) {
            setError("Drinks pr. billet må ikke være negativ");
            return;
        }

        try {
            setIsSaving(true);
            setMessage(null);
            setError(null);

            const payload = {
                eventId,
                collectionName: collectionName.trim(),
                ticketCount,
                maxDrinksPerTicket,
                bar: Number(bar),
                validDate: validDate ? validDate : null
            };

            const res = await fetch("/api/admin/drink-ticket-collections", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const msg = await readErrorMessage(
                    res,
                    `Kunne ikke oprette collection (${res.status})`
                );
                throw new Error(msg);
            }

            const result = await res.json();

            setMessage(
                `Collection oprettet med ${result.ticketCount ?? ticketCount} billetter`
            );

            setCollectionName("");
            setValidDate("");

            await loadCollections(eventId);

            if (result.collectionId) {
                setSelectedCollectionId(Number(result.collectionId));
            }
        } catch (err: any) {
            console.error("Fejl ved oprettelse:", err);
            setError(err?.message || "Fejl ved oprettelse");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">Billet Collections</div>
                    <div className="meta">Opret enkeltbilletter samlet i collections</div>
                </div>

                <div className="content">
                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    {message && (
                        <div className="statusBadge" style={{ padding: 8, marginBottom: 16 }}>
                            {message}
                        </div>
                    )}

                    <div className="sectionTitle">Opret collection</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Event</label>
                            <Select
                                placeholder="Vælg event"
                                value={eventId ? String(eventId) : null}
                                onChange={(val) => setEventId(val ? Number(val) : 0)}
                                data={events.map((e) => ({
                                    value: String(e.eventID),
                                    label: e.eventName || `Event ${e.eventID}`
                                }))}
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Navn på collection</label>
                            <input
                                type="text"
                                value={collectionName}
                                onChange={(e) => setCollectionName(e.target.value)}
                                placeholder="Fx SED"
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Bar</label>
                            <Select
                                placeholder="Vælg bar"
                                value={bar}
                                onChange={setBar}
                                data={[
                                    { value: String(Bar.BAR1), label: getBarName(Bar.BAR1) },
                                    { value: String(Bar.BAR2), label: getBarName(Bar.BAR2) },
                                    { value: String(Bar.BAR3), label: getBarName(Bar.BAR3) }
                                ]}
                            />
                        </div>
                    </div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Antal billetter</label>
                            <input
                                type="number"
                                min={1}
                                value={ticketCount}
                                onChange={(e) => setTicketCount(toNonNegativeNumber(e.target.value))}
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Drinks pr. billet</label>
                            <input
                                type="number"
                                min={0}
                                value={maxDrinksPerTicket}
                                onChange={(e) => setMaxDrinksPerTicket(toNonNegativeNumber(e.target.value))}
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Gyldig dato (valgfri)</label>
                            <input
                                type="date"
                                value={validDate}
                                onChange={(e) => setValidDate(e.target.value)}
                            />
                            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
                                Tom = virker alle dage
                            </div>
                        </div>
                    </div>

                    <div className="stat" style={{ padding: 16, marginTop: 12 }}>
                        <button
                            type="button"
                            style={{ width: "100%" }}
                            onClick={submit}
                            disabled={isSaving}
                        >
                            {isSaving ? "Opretter..." : "Opret collection"}
                        </button>
                    </div>

                    {eventId > 0 && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Collections
                            </div>

                            <div className="stat" style={{ padding: 16, marginTop: 12 }}>
                                <Select
                                    searchable
                                    clearable
                                    placeholder={isLoadingCollections ? "Henter collections..." : "Vælg collection"}
                                    value={selectedCollectionId ? String(selectedCollectionId) : null}
                                    onChange={(val) => setSelectedCollectionId(val ? Number(val) : 0)}
                                    data={collections.map((c) => ({
                                        value: String(c.drinkTicketCollectionId),
                                        label: `${c.collectionName} - ${getBarName(c.bar)} - ${c.ticketCount} stk - ${formatDate(c.validDate)}`
                                    }))}
                                />
                            </div>
                        </>
                    )}

                    {selectedCollection && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Billetter i {selectedCollection.collectionName}
                            </div>

                            <div style={{ marginTop: 8, marginBottom: 12 }}>
                                Gyldighed: <strong>{formatDate(selectedCollection.validDate)}</strong>
                            </div>

                            <div className="stat" style={{ padding: 0, marginTop: 12 }}>
                                {isLoadingTickets ? (
                                    <div style={{ padding: 16 }}>Henter billetter...</div>
                                ) : (
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                <th style={thLeft}>Billetkode</th>
                                                <th style={th}>Nr.</th>
                                                <th style={th}>Bar</th>
                                                <th style={th}>Drinks</th>
                                                <th style={th}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tickets.map((ticket) => (
                                                <tr key={ticket.drinkTicketId}>
                                                    <td style={tdLeft}>{ticket.ticketCode}</td>
                                                    <td style={td}>{ticket.ticketNumber}</td>
                                                    <td style={td}>{getBarName(ticket.bar)}</td>
                                                    <td style={td}>{ticket.maxDrinks}</td>
                                                    <td style={{ ...td, ...getTicketStatusStyle(ticket.isActive) }}>
                                                        {getTicketStatusLabel(ticket.isActive)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const th = {
    padding: "12px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    textAlign: "center" as const
};

const thLeft = {
    ...th,
    textAlign: "left" as const
};

const td = {
    padding: "10px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    textAlign: "center" as const
};

const tdLeft = {
    ...td,
    textAlign: "left" as const
};

export default AdminDrinkTicketCollections;